/**
 * Fiado de barrio: la libreta del tendero.
 *
 * Dos cosas que se ven a simple vista y que son las que importan:
 *
 *  · **Comprar fiado no saca dinero de la cuenta.** Solo sube lo que debes. La
 *    pantalla lo dice explícitamente al registrar, porque es la confusión número
 *    uno: la gente ve la compra y cree que le quitaron la plata.
 *  · **La libreta se puede cotejar con el papel del tendero.** Muestra cada
 *    renglón con el saldo después de cada uno, que es exactamente lo que hay en la
 *    libreta de la tienda.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Plus, Store } from 'lucide-react';
import { useState } from 'react';

import { formatCOP, formatDateLabel, parseAmountFromText, todayISO, toLocalTimeHHmm } from '@eldescuadre/core';

import {
  createFiadoStore,
  createTransaction,
  deleteFiadoStore,
  getAccounts,
  getCategories,
  getFiadoLedger,
  getFiadoStores,
  getFiadoSummary,
} from '../lib/endpoints';
import { ApiError } from '../lib/api';
import {
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Loading,
  Modal,
  Money,
  Note,
  Progress,
  Stat,
  SubmitButton,
} from '../components/ui';

export function FiadoPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [openStore, setOpenStore] = useState<string | null>(null);
  const [movement, setMovement] = useState<{ storeId: string; kind: 'FIADO_PURCHASE' | 'FIADO_PAYMENT' } | null>(null);

  const summary = useQuery({ queryKey: ['fiado', 'summary'], queryFn: getFiadoSummary });
  const stores = useQuery({ queryKey: ['fiado', 'stores'], queryFn: getFiadoStores });

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['fiado'] }),
      queryClient.invalidateQueries({ queryKey: ['accounts'] }),
      queryClient.invalidateQueries({ queryKey: ['summary'] }),
      queryClient.invalidateQueries({ queryKey: ['transactions'] }),
    ]);
  };

  const data = summary.data;

  return (
    <div className="space-y-4 animate-ed-fade-in">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">Fiado</h1>
          <p className="mt-0.5 text-sm text-ed-text-muted">
            Lo que debes en las tiendas del barrio.
          </p>
        </div>
        <button
          type="button"
          className="ed-btn-primary !min-h-[44px] !w-auto shrink-0 !px-4"
          onClick={() => setCreating(true)}
        >
          <Plus size={18} aria-hidden="true" />
          Tienda
        </button>
      </header>

      {summary.isLoading && <Loading label="Cargando tu fiado…" />}
      {summary.isError && <ErrorNote message="No se pudo cargar el fiado." />}

      {data && (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <Stat
              label="Debes en total"
              value={<Money amount={data.totalDebt} />}
              hint={
                data.overLimit > 0 ? (
                  <span className="text-ed-coral-light">
                    Pasaste el cupo en <Money amount={data.overLimit} />
                  </span>
                ) : (
                  `${data.stores.length} ${data.stores.length === 1 ? 'tienda' : 'tiendas'}`
                )
              }
              tone={data.overLimit > 0 ? 'negative' : 'gold'}
            />

            <Stat
              label="Este mes"
              value={<Money amount={data.monthPurchases} />}
              hint={
                <>
                  Comprado · abonado <Money amount={data.monthPayments} />
                </>
              }
            />
          </div>

          {/* Los tres bolsillos del mes: mercado, mecato y licor. */}
          {(data.monthByPocket.market > 0 ||
            data.monthByPocket.snack > 0 ||
            data.monthByPocket.leisure > 0) && (
            <Card title="En qué se fue el fiado de este mes">
              <ul className="space-y-3">
                {[
                  { key: 'market', label: 'Mercado', value: data.monthByPocket.market, color: '#10B981' },
                  { key: 'snack', label: 'Mecato', value: data.monthByPocket.snack, color: '#ECD082' },
                  { key: 'leisure', label: 'Licor y ocio', value: data.monthByPocket.leisure, color: '#EB525B' },
                ]
                  .filter((row) => row.value > 0)
                  .map((row) => {
                    const total =
                      data.monthByPocket.market +
                      data.monthByPocket.snack +
                      data.monthByPocket.leisure;
                    const percent = total > 0 ? (row.value / total) * 100 : 0;
                    return (
                      <li key={row.key}>
                        <div className="mb-1 flex items-baseline justify-between gap-2 text-sm">
                          <span className="flex items-center gap-2">
                            <span
                              className="h-2.5 w-2.5 rounded-full"
                              style={{ backgroundColor: row.color }}
                              aria-hidden="true"
                            />
                            {row.label}
                          </span>
                          <span className="flex items-baseline gap-2">
                            <Money amount={row.value} className="font-semibold" />
                            <span className="text-xs text-ed-text-muted">
                              {Math.round(percent)} %
                            </span>
                          </span>
                        </div>
                        <Progress
                          percent={percent}
                          tone={row.key === 'leisure' ? 'coral' : row.key === 'snack' ? 'gold' : 'success'}
                          label={`${row.label}: ${Math.round(percent)} por ciento`}
                        />
                      </li>
                    );
                  })}
              </ul>
              <p className="mt-3 text-xs text-ed-text-muted">
                El fiado se separa en tres bolsillos porque no es lo mismo deber el mercado
                que deber el mecato. Verlo así ayuda a decidir.
              </p>
            </Card>
          )}

          {/* Tiendas */}
          <Card title="Tus tiendas">
            {stores.isLoading && <Loading />}

            {stores.data && stores.data.stores.length === 0 && (
              <EmptyState
                title="Todavía no tienes tiendas"
                message="Añade la tienda donde sueles fiar y podrás anotar las compras y los abonos. La libreta se lleva sola."
                action={
                  <button
                    type="button"
                    className="ed-btn-primary !w-auto !px-4"
                    onClick={() => setCreating(true)}
                  >
                    Añadir la primera
                  </button>
                }
              />
            )}

            {stores.data && stores.data.stores.length > 0 && (
              <ul className="space-y-2">
                {stores.data.stores.map((store) => (
                  <li key={store.id}>
                    <div className="rounded-ed border border-ed-bg-border/30 bg-ed-bg-elevated/40 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <button
                          type="button"
                          className="min-w-0 flex-1 text-left"
                          onClick={() => setOpenStore(store.id)}
                        >
                          <span className="flex items-center gap-2">
                            <Store size={15} className="shrink-0 text-ed-text-muted" aria-hidden="true" />
                            <span className="truncate font-medium">{store.name}</span>
                            <ChevronRight size={14} className="shrink-0 text-ed-text-muted" aria-hidden="true" />
                          </span>
                          <span className="mt-0.5 block text-xs text-ed-text-muted">
                            {store.limit !== null ? (
                              <>
                                Cupo {formatCOP(store.limit)}
                                {store.available !== null && <> · te queda {formatCOP(store.available)}</>}
                              </>
                            ) : (
                              'Sin cupo acordado'
                            )}
                          </span>
                        </button>

                        <Money
                          amount={store.balance}
                          className={`shrink-0 font-display text-lg font-bold ${
                            store.balance > 0 ? 'text-ed-coral-light' : 'text-ed-success'
                          }`}
                        />
                      </div>

                      {store.limit !== null && store.limit > 0 && (
                        <div className="mt-2">
                          <Progress
                            percent={(store.balance / store.limit) * 100}
                            tone={store.overLimit ? 'coral' : 'gold'}
                            label={`${store.name}: ${Math.round((store.balance / store.limit) * 100)} por ciento del cupo usado`}
                          />
                        </div>
                      )}

                      <div className="mt-3 flex gap-2">
                        <button
                          type="button"
                          className="ed-btn-secondary !min-h-[40px] flex-1 text-xs"
                          onClick={() => setMovement({ storeId: store.id, kind: 'FIADO_PURCHASE' })}
                        >
                          Compré fiado
                        </button>
                        <button
                          type="button"
                          className="ed-btn-secondary !min-h-[40px] flex-1 text-xs"
                          disabled={store.balance <= 0}
                          onClick={() => setMovement({ storeId: store.id, kind: 'FIADO_PAYMENT' })}
                        >
                          Aboné
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <p className="px-1 pb-2 text-center text-xs text-ed-text-muted">
            Comprar fiado sube lo que debes pero <strong>no saca dinero de tus cuentas</strong>.
            El abono sí lo saca, y no cuenta como gasto nuevo: el gasto ya se contó el día de la
            compra.
          </p>
        </>
      )}

      {creating && <NewStoreModal onClose={() => setCreating(false)} onCreated={refresh} />}

      {openStore && (
        <LedgerModal
          storeId={openStore}
          onClose={() => setOpenStore(null)}
          onChanged={refresh}
        />
      )}

      {movement && (
        <FiadoMovementModal
          storeId={movement.storeId}
          kind={movement.kind}
          onClose={() => setMovement(null)}
          onSaved={async () => {
            setMovement(null);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

/** Crear una tienda con su cupo de confianza. */
function NewStoreModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => Promise<void>;
}): React.JSX.Element {
  const [name, setName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [phone, setPhone] = useState('');
  const [limitText, setLimitText] = useState('');

  const create = useMutation({
    mutationFn: () =>
      createFiadoStore({
        name: name.trim(),
        ownerName: ownerName.trim() || undefined,
        phone: phone.trim() || undefined,
        limit: parseAmountFromText(limitText) ?? undefined,
      }),
    onSuccess: async () => {
      await onCreated();
      onClose();
    },
  });

  const error = create.error instanceof ApiError ? create.error : null;

  return (
    <Modal
      open
      onClose={onClose}
      title="Nueva tienda"
      footer={
        <SubmitButton
          type="button"
          pending={create.isPending}
          disabled={name.trim().length === 0}
          onClick={() => create.mutate()}
        >
          Guardar tienda
        </SubmitButton>
      }
    >
      <div className="space-y-4">
        <Field label="Nombre de la tienda" htmlFor="nombre-tienda">
          <input
            id="nombre-tienda"
            className="ed-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Tienda Don Pedro"
            autoComplete="off"
          />
        </Field>

        <Field label="Dueño (opcional)" htmlFor="dueno">
          <input
            id="dueno"
            className="ed-input"
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
            placeholder="Don Pedro"
            autoComplete="off"
          />
        </Field>

        <Field label="Teléfono (opcional)" htmlFor="telefono" hint="Para poder escribirle si hace falta.">
          <input
            id="telefono"
            className="ed-input"
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="300 123 4567"
            autoComplete="off"
          />
        </Field>

        <Field
          label="Cupo acordado (opcional)"
          htmlFor="cupo"
          hint="El máximo hasta el que la tienda te fía. Te avisaremos al pasarte."
        >
          <input
            id="cupo"
            className="ed-input ed-money"
            inputMode="numeric"
            value={limitText}
            onChange={(e) => setLimitText(e.target.value)}
            placeholder="200.000"
            autoComplete="off"
          />
        </Field>

        {error && <ErrorNote message={error.detailMessage} />}
      </div>
    </Modal>
  );
}

/** La libreta de una tienda, con sus acciones. */
function LedgerModal({
  storeId,
  onClose,
  onChanged,
}: {
  storeId: string;
  onClose: () => void;
  onChanged: () => Promise<void>;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [movement, setMovement] = useState<'FIADO_PURCHASE' | 'FIADO_PAYMENT' | null>(null);

  const stores = useQuery({ queryKey: ['fiado', 'stores'], queryFn: getFiadoStores });
  const store = stores.data?.stores.find((s) => s.id === storeId);

  const ledger = useQuery({
    queryKey: ['fiado', 'ledger', storeId],
    queryFn: () => getFiadoLedger(storeId),
  });

  const remove = useMutation({
    mutationFn: () => deleteFiadoStore(storeId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['fiado'] });
      onClose();
    },
  });

  const removeError = remove.error instanceof ApiError ? remove.error : null;

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title={store?.name ?? 'Libreta'}
        footer={
          <div className="flex gap-2">
            <button
              type="button"
              className="ed-btn-secondary !min-h-[44px] flex-1 text-sm"
              onClick={() => setMovement('FIADO_PURCHASE')}
            >
              Compré fiado
            </button>
            <button
              type="button"
              className="ed-btn-primary !min-h-[44px] flex-1 text-sm"
              disabled={(store?.balance ?? 0) <= 0}
              onClick={() => setMovement('FIADO_PAYMENT')}
            >
              Aboné
            </button>
          </div>
        }
      >
        {store && (
          <div className="mb-4 grid grid-cols-2 gap-3">
            <div className="rounded-ed border border-ed-bg-border/30 bg-ed-bg-elevated/40 p-3">
              <p className="text-xs uppercase tracking-wide text-ed-text-muted">Debes</p>
              <Money amount={store.balance} className="font-display text-lg font-bold text-ed-coral-light" />
            </div>
            <div className="rounded-ed border border-ed-bg-border/30 bg-ed-bg-elevated/40 p-3">
              <p className="text-xs uppercase tracking-wide text-ed-text-muted">Cupo libre</p>
              <p className="font-display text-lg font-bold">
                {store.available !== null ? formatCOP(store.available) : '—'}
              </p>
            </div>
          </div>
        )}

        {ledger.isLoading && <Loading label="Abriendo la libreta…" />}

        {ledger.data && ledger.data.entries.length === 0 && (
          <EmptyState
            title="La libreta está en blanco"
            message="Cuando anotes la primera compra, aparecerá aquí con el saldo después de cada renglón."
          />
        )}

        {ledger.data && ledger.data.entries.length > 0 && (
          <ul className="divide-y divide-ed-bg-border/25">
            {ledger.data.entries.map((entry) => (
              <li key={entry.id} className="flex items-start justify-between gap-3 py-2.5">
                <span className="min-w-0">
                  <span className="block text-sm">
                    {entry.kind === 'PURCHASE' ? 'Compra fiada' : 'Abono'}
                  </span>
                  <span className="block text-xs text-ed-text-muted">
                    {formatDateLabel(entry.date)}
                    {entry.notes && <> · {entry.notes}</>}
                  </span>
                  {entry.kind === 'PURCHASE' &&
                    (entry.marketAmount > 0 || entry.snackAmount > 0 || entry.leisureAmount > 0) && (
                      <span className="mt-0.5 block text-xs text-ed-text-muted">
                        {[
                          entry.marketAmount > 0 && `Mercado ${formatCOP(entry.marketAmount)}`,
                          entry.snackAmount > 0 && `Mecato ${formatCOP(entry.snackAmount)}`,
                          entry.leisureAmount > 0 && `Licor ${formatCOP(entry.leisureAmount)}`,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    )}
                </span>

                <span className="shrink-0 text-right">
                  <Money
                    amount={entry.kind === 'PURCHASE' ? entry.amount : -entry.amount}
                    signed
                    className="block text-sm font-semibold"
                  />
                  <span className="block text-xs text-ed-text-muted">
                    saldo <Money amount={entry.balanceAfter} />
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}

        {removeError && (
          <div className="mt-4">
            <ErrorNote message={removeError.message} />
          </div>
        )}

        {store && store.balance <= 0 && (
          <div className="mt-4">
            <button
              type="button"
              className="ed-btn-ghost w-full text-xs text-ed-coral-light"
              onClick={() => {
                if (window.confirm(`¿Eliminar «${store.name}»? No tiene saldo pendiente.`)) {
                  remove.mutate();
                }
              }}
            >
              Eliminar esta tienda
            </button>
          </div>
        )}
      </Modal>

      {movement && (
        <FiadoMovementModal
          storeId={storeId}
          kind={movement}
          onClose={() => setMovement(null)}
          onSaved={async () => {
            setMovement(null);
            await onChanged();
            await queryClient.invalidateQueries({ queryKey: ['fiado', 'ledger', storeId] });
          }}
        />
      )}
    </>
  );
}

/** Anotar una compra fiada o un abono. */
function FiadoMovementModal({
  storeId,
  kind,
  onClose,
  onSaved,
}: {
  storeId: string;
  kind: 'FIADO_PURCHASE' | 'FIADO_PAYMENT';
  onClose: () => void;
  onSaved: () => Promise<void>;
}): React.JSX.Element {
  const [text, setText] = useState('');
  const [accountId, setAccountId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [notes, setNotes] = useState('');

  const stores = useQuery({ queryKey: ['fiado', 'stores'], queryFn: getFiadoStores });
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: () => getAccounts() });
  // Las categorías de gasto, para poder etiquetar el abono.
  const categories = useQuery({
    queryKey: ['categories', 'EXPENSE'],
    queryFn: () => getCategories('EXPENSE'),
    // Solo hacen falta para el abono; en una compra no se pregunta.
    enabled: kind === 'FIADO_PAYMENT',
  });

  const store = stores.data?.stores.find((s) => s.id === storeId);
  const amount = parseAmountFromText(text) ?? 0;

  const isPurchase = kind === 'FIADO_PURCHASE';
  const chosenAccountId = accountId || accounts.data?.accounts[0]?.id || '';

  const problem = !isPurchase && amount > (store?.balance ?? 0)
    ? `No puedes abonar más de lo que debes. El saldo es ${formatCOP(store?.balance ?? 0)}.`
    : null;

  const save = useMutation({
    mutationFn: () => {
      const key = crypto.randomUUID();
      return createTransaction(
        {
          kind,
          amount,
          currency: 'COP',
          date: todayISO(),
          time: toLocalTimeHHmm(new Date()),
          fiadoStoreId: storeId,
          notes: notes.trim() || undefined,
          // Comprar fiado no toca ninguna cuenta; el abono sí.
          accountId: isPurchase ? undefined : chosenAccountId,
          // La categoría solo se pregunta en el abono, y es opcional.
          categoryId: isPurchase ? undefined : categoryId || undefined,
          idempotencyKey: key,
        },
        key,
      );
    },
    onSuccess: onSaved,
  });

  const error = save.error instanceof ApiError ? save.error : null;

  return (
    <Modal
      open
      onClose={onClose}
      title={isPurchase ? `Compra fiada en ${store?.name ?? 'la tienda'}` : `Abono a ${store?.name ?? 'la tienda'}`}
      footer={
        <SubmitButton
          type="button"
          pending={save.isPending}
          disabled={amount <= 0 || problem !== null || (!isPurchase && !chosenAccountId)}
          onClick={() => save.mutate()}
        >
          {isPurchase ? 'Anotar la compra' : 'Registrar el abono'}
        </SubmitButton>
      }
    >
      <div className="space-y-4">
        <Field
          label="¿Cuánto?"
          htmlFor="importe-fiado"
          hint="Puedes escribirlo como quieras: «25k», «25.000», «25 mil»."
        >
          <input
            id="importe-fiado"
            className="ed-input ed-money"
            inputMode="numeric"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="25.000"
            autoFocus
            autoComplete="off"
          />
        </Field>

        {amount > 0 && (
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="ed-chip border-ed-gold/40 bg-ed-gold/10 text-ed-gold">
              {formatCOP(amount)}
            </span>
            <span className="ed-chip border-ed-bg-border/50 text-ed-text-silver">
              {isPurchase ? (
                <>
                  Deuda nueva: {formatCOP((store?.balance ?? 0) + amount)}
                </>
              ) : (
                <>
                  Quedará debiendo: {formatCOP(Math.max(0, (store?.balance ?? 0) - amount))}
                </>
              )}
            </span>
          </div>
        )}

        {isPurchase ? (
          <Note>
            Esta compra <strong>no saca dinero de tus cuentas</strong>. Sube lo que debes en la
            tienda y cuenta como gasto del mes. El dinero saldrá cuando abones.
          </Note>
        ) : (
          <Field label="¿De qué cuenta sale el dinero?" htmlFor="cuenta-fiado">
            <select
              id="cuenta-fiado"
              className="ed-input"
              value={chosenAccountId}
              onChange={(e) => setAccountId(e.target.value)}
            >
              <option value="">Elige una cuenta…</option>
              {(accounts.data?.accounts ?? []).map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name} · {formatCOP(account.balance)}
                </option>
              ))}
            </select>
          </Field>
        )}

        {/*
          La categoría del abono.
          
          Solo se pregunta en un abono, no en una compra: la compra ya la pide el
          formulario principal. Y es **opcional**, porque un abono no es un gasto: es
          pagar algo que ya se contó. Sirve para identificarlo en los informes, que es
          justo lo que faltaba: en el archivo salía con la categoría en blanco.
        */}
        {!isPurchase && (
          <Field
            label="Categoría (opcional)"
            htmlFor="categoria-fiado"
            hint="Para reconocer el abono en los informes. No cuenta como gasto otra vez."
          >
            <select
              id="categoria-fiado"
              className="ed-input"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <option value="">Sin categoría</option>
              {(categories.data?.categories ?? []).map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </Field>
        )}

        <Field label="Notas (opcional)" htmlFor="notas-fiado">
          <input
            id="notas-fiado"
            className="ed-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Qué llevaste, para qué…"
            autoComplete="off"
          />
        </Field>

        {problem && <ErrorNote message={problem} />}
        {error && <ErrorNote message={error.detailMessage} />}
      </div>
    </Modal>
  );
}
